import React from 'react';
import Icon from '../../../components/AppIcon';

const TransparencySection = () => {
  const transparencyAreas = [
    {
      id: 1,
      title: "Fee Structure Transparency",
      description: "Clear breakdown of all service fees, commission structures, and payment terms with no hidden costs or surprise charges.",
      icon: "DollarSign",
      color: "primary",
      details: [
        "Standard commission: 5-10% of transfer value",
        "Youth player development: Fixed monthly retainer",
        "Consultation services: Hourly rate structure",
        "Success-based bonuses clearly defined upfront"
      ]
    },
    {
      id: 2,
      title: "Process Documentation",
      description: "Comprehensive documentation of every step in the transfer process, from initial contact to contract completion and registration.",
      icon: "FileText",
      color: "secondary",
      details: [
        "Initial player assessment and evaluation reports",
        "Club presentation materials and pitch decks",
        "Negotiation progress tracking and updates",
        "Contract review and legal documentation"
      ]
    },
    {
      id: 3,
      title: "Ethical Standards",
      description: "Commitment to ethical practices in all dealings, prioritizing player welfare and fair treatment above commercial interests.",
      icon: "Heart",
      color: "success",
      details: [
        "Player-first approach in all negotiations",
        "No conflicts of interest or dual representation",
        "Honest assessment of opportunities and risks",
        "Respect for existing contractual obligations"
      ]
    },
    {
      id: 4,
      title: "Data Protection & Privacy",
      description: "Rigorous data protection measures ensuring player information, club negotiations, and sensitive documents remain secure and confidential.",
      icon: "Lock",
      color: "accent",
      details: [
        "GDPR compliant data handling procedures",
        "Encrypted communication channels",
        "Secure document storage and sharing",
        "Regular security audits and updates"
      ]
    },
    {
      id: 5,
      title: "Conflict Resolution",
      description: "Established procedures for addressing disputes, concerns, or complaints with fair and transparent resolution mechanisms.",
      icon: "Scale",
      color: "warning",
      details: [
        "Clear escalation procedures for issues",
        "Independent mediation options available",
        "Documented complaint handling process",
        "Regular client satisfaction surveys"
      ]
    },
    {
      id: 6,
      title: "Performance Reporting",
      description: "Regular reporting on transfer activities, success metrics, and market performance with honest assessment of outcomes.",
      icon: "BarChart3",
      color: "secondary",
      details: [
        "Monthly activity reports for active clients",
        "Quarterly market analysis and insights",
        "Annual performance review meetings",
        "Real-time transfer status updates"
      ]
    }
  ];

  return (
    <section className="py-12 md:py-16 lg:py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-10 md:mb-12 lg:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-4 md:mb-6">
            <Icon name="Eye" size={20} color="var(--color-primary)" />
            <span className="text-sm md:text-base font-medium text-primary">Transparency Commitment</span>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-4 md:mb-6 font-headline">
            Our Transparency Framework
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto">
            ProX11Market operates with complete transparency in all aspects of our business, ensuring trust and confidence for players, clubs, and partners.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 lg:gap-10">
          {transparencyAreas?.map((area) => (
            <div
              key={area?.id}
              className="bg-card rounded-xl border border-border p-6 md:p-8 hover:shadow-lg transition-all duration-300 group"
            >
              <div className="flex items-start gap-4 mb-6">
                <div className={`w-14 h-14 md:w-16 md:h-16 bg-${area?.color}/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                  <Icon name={area?.icon} size={28} color={`var(--color-${area?.color})`} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg md:text-xl font-bold text-foreground mb-2 font-headline">
                    {area?.title}
                  </h3>
                  <p className="text-sm md:text-base text-muted-foreground">
                    {area?.description}
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                {area?.details?.map((detail, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <Icon name="CheckCircle2" size={18} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                    <span className="text-sm md:text-base text-foreground">{detail}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 md:mt-12 lg:mt-16 bg-gradient-to-br from-primary/5 to-secondary/5 rounded-xl p-6 md:p-8 lg:p-10 border border-primary/20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground mb-4 font-headline">
                Annual Transparency Report
              </h3>
              <p className="text-sm md:text-base text-muted-foreground mb-6">
                We publish comprehensive annual reports detailing our operations, transfer activities, compliance metrics, and financial performance. These reports demonstrate our commitment to accountability and transparency.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 px-4 py-2 bg-card rounded-lg border border-border">
                  <Icon name="FileText" size={20} color="var(--color-primary)" />
                  <span className="text-sm font-medium text-foreground">2025 Report Available</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-card rounded-lg border border-border">
                  <Icon name="Download" size={20} color="var(--color-secondary)" />
                  <span className="text-sm font-medium text-foreground">Download PDF</span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-card rounded-lg p-4 md:p-6 border border-border">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm md:text-base text-muted-foreground">Successful Transfers</span>
                  <span className="text-xl md:text-2xl font-bold text-success">127</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div className="bg-success h-2 rounded-full" style={{ width: '95%' }}></div>
                </div>
              </div>

              <div className="bg-card rounded-lg p-4 md:p-6 border border-border">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm md:text-base text-muted-foreground">Client Satisfaction</span>
                  <span className="text-xl md:text-2xl font-bold text-primary">98%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div className="bg-primary h-2 rounded-full" style={{ width: '98%' }}></div>
                </div>
              </div>

              <div className="bg-card rounded-lg p-4 md:p-6 border border-border">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm md:text-base text-muted-foreground">Compliance Rate</span>
                  <span className="text-xl md:text-2xl font-bold text-secondary">100%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div className="bg-secondary h-2 rounded-full" style={{ width: '100%' }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TransparencySection;